import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { ScrollArea } from "./ui/scroll-area";

interface DebugPanelProps {
  debugMode: boolean;
  error: string | null;
  debugError: Error | null;
  isLoading: boolean;
  rawWorkoutLogs: any[];
  workoutHistory: any[];
}

export const DebugPanel: React.FC<DebugPanelProps> = ({
  debugMode,
  error,
  debugError,
  isLoading,
  rawWorkoutLogs,
  workoutHistory,
}) => {
  if (!debugMode) return null;
  return (
    <Card className="mb-4 border-red-200 dark:border-red-800">
      <CardHeader className="bg-red-50 dark:bg-red-900/20 pb-2">
        <CardTitle className="text-red-700 dark:text-red-300 flex justify-between items-center">
          <span>Debug Information</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.location.reload()}
            className="text-xs"
          >
            Reload Page
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        <Collapsible className="w-full">
          <CollapsibleTrigger asChild>
            <Button variant="outline" size="sm" className="w-full mb-2">
              {error || debugError ? "⚠️ Error Details" : "✅ Status"}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="p-2 bg-muted rounded-md mb-4">
              <h4 className="font-semibold mb-1">Status:</h4>
              <p className="text-sm">
                {isLoading ? "Loading..." : error ? "Error" : "Loaded"}
              </p>
              {(error || debugError) && (
                <>
                  <h4 className="font-semibold mt-2 mb-1">Error:</h4>
                  <p className="text-sm text-red-500">
                    {error || debugError?.message}
                  </p>
                  {debugError?.stack && (
                    <pre className="text-xs mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded overflow-auto">
                      {debugError.stack}
                    </pre>
                  )}
                </>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
        <Collapsible className="w-full">
          <CollapsibleTrigger asChild>
            <Button variant="outline" size="sm" className="w-full mb-2">
              Raw Workout Logs ({rawWorkoutLogs.length})
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <ScrollArea className="h-[300px] rounded-md border p-2">
              <pre className="text-xs whitespace-pre-wrap">
                {JSON.stringify(rawWorkoutLogs, null, 2)}
              </pre>
            </ScrollArea>
          </CollapsibleContent>
        </Collapsible>
        <Collapsible className="w-full">
          <CollapsibleTrigger asChild>
            <Button variant="outline" size="sm" className="w-full mb-2">
              Processed Workout History ({workoutHistory.length})
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <ScrollArea className="h-[300px] rounded-md border p-2">
              <pre className="text-xs whitespace-pre-wrap">
                {JSON.stringify(workoutHistory, null, 2)}
              </pre>
            </ScrollArea>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
};
